import type { MessageReaction, ReportReason, VisibilityLevel } from '@anidex/shared/economy';
import type { PresenceStatus } from '@anidex/shared/social';
import { achievementTierTitle, achievementTitle, rarityName } from '../../i18n/catalog';
import i18n from '../../i18n';
import { formatNumber } from '../../services/format';
import { CATALOG_DELETE_FIELD } from '@anidex/shared/staff';

function amountLabel(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatNumber(value) : '?';
}

function social(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, { ns: 'social', ...options });
}

export const PRESENCE_LABELS: Record<PresenceStatus, string> = {
  get online() {
    return social('presence.online');
  },
  get away() {
    return social('presence.away');
  },
  get offline() {
    return social('presence.offline');
  },
};

export const VISIBILITY_LABELS: Record<VisibilityLevel, string> = {
  get public() {
    return social('visibility.public');
  },
  get friends() {
    return social('visibility.friends');
  },
  get private() {
    return social('visibility.private');
  },
};

export const REPORT_LABELS: Record<ReportReason, string> = {
  get spam() {
    return social('reports.spam');
  },
  get harassment() {
    return social('reports.harassment');
  },
  get inappropriate() {
    return social('reports.inappropriate');
  },
  get impersonation() {
    return social('reports.impersonation');
  },
  get other() {
    return social('reports.other');
  },
};

export const REACTION_LABELS: Record<MessageReaction, string> = {
  heart: '❤️',
  fire: '🔥',
  star: '⭐',
  spark: '✨',
  laugh: '😂',
  wow: '😮',
};

export const NOTIF_PREF_LABELS = {
  get friend_request() {
    return social('prefs.friend_request');
  },
  get friend_accepted() {
    return social('prefs.friend_accepted');
  },
  get message() {
    return social('prefs.message');
  },
  get reward() {
    return social('prefs.reward');
  },
  get market() {
    return social('prefs.market');
  },
  get trade() {
    return social('prefs.trade');
  },
  get free_booster() {
    return social('prefs.free_booster');
  },
  get login_calendar() {
    return social('prefs.login_calendar');
  },
};

function cardName(data: Record<string, unknown>, fallbackKey: string): string {
  return typeof data.characterName === 'string' && data.characterName ? data.characterName : social(fallbackKey);
}

export function activityLabel(type: string, data: Record<string, unknown>, username?: string): string {
  const who = username ?? social('activity.someone');
  switch (type) {
    case 'level_up':
      return social('activity.levelUp', { who, level: String(data.level ?? '?') });
    case 'achievement': {
      const title =
        typeof data.id === 'string'
          ? typeof data.tier === 'number'
            ? achievementTierTitle(data.id, data.tier, String(data.title ?? ''))
            : achievementTitle(data.id, String(data.title ?? ''))
          : String(data.title ?? social('activity.achievementFallback'));
      return social('activity.achievement', { who, title: title || social('activity.achievementFallback') });
    }
    case 'rare_drop': {
      const rarity = typeof data.rarity === 'string' ? rarityName(data.rarity) : social('activity.rareFallback');
      const name = String(data.name ?? social('activity.cardFallback'));
      return social('activity.rareDrop', { who, name, rarity });
    }
    case 'anime_completed':
      return social('activity.animeCompleted', {
        who,
        title: String(data.title ?? social('activity.animeFallback')),
      });
    default:
      return social('activity.generic', { who });
  }
}

export function notificationTitle(type: string, data: Record<string, unknown>, actor: string | null): string {
  const who = actor ?? social('notifications.someone');
  switch (type) {
    case 'friend_request':
      return social('notifications.friendRequest', { who });
    case 'friend_accepted':
      return social('notifications.friendAccepted', { who });
    case 'referral_joined':
      return social('notifications.referralJoined', { who });
    case 'message':
      return data.preview
        ? social('notifications.messagePreview', { who, preview: String(data.preview) })
        : social('notifications.message', { who });
    case 'reward': {
      if (data.kind === 'achievements' && typeof data.count === 'number') {
        return social('notifications.achievementsMany', { count: data.count });
      }
      const title =
        data.kind === 'achievement' && typeof data.id === 'string'
          ? achievementTitle(data.id, String(data.title ?? data.label ?? ''))
          : String(data.title ?? data.label ?? '');
      return title ? social('notifications.reward', { title }) : social('notifications.rewardEmpty');
    }
    case 'free_boosters_full':
      return social('notifications.freeBoosters');
    case 'login_claimable':
      return social('notifications.login');
    case 'listing_wish':
      return data.characterName
        ? social('notifications.listingWishNamed', { name: String(data.characterName) })
        : social('notifications.listingWish');
    case 'auction_wish':
      return data.characterName
        ? social('notifications.auctionWishNamed', { name: String(data.characterName) })
        : social('notifications.auctionWish');
    case 'listing_favorite':
      return data.characterName
        ? social('notifications.listingFavoriteNamed', { name: String(data.characterName) })
        : social('notifications.listingFavorite');
    case 'auction_favorite':
      return data.characterName
        ? social('notifications.auctionFavoriteNamed', { name: String(data.characterName) })
        : social('notifications.auctionFavorite');
    case 'listing_sold':
      return data.characterName
        ? social('notifications.listingSoldNamed', { who, name: String(data.characterName) })
        : social('notifications.listingSold', { who });
    case 'listing_expired':
      return social('notifications.listingExpired', { name: cardName(data, 'notifications.card') });
    case 'auction_bid':
      return data.characterName
        ? social('notifications.auctionBidNamed', { who, amount: amountLabel(data.amount), name: String(data.characterName) })
        : social('notifications.auctionBid', { who, amount: amountLabel(data.amount) });
    case 'auction_outbid':
      return data.buyout
        ? social('notifications.outbidBuyout', { who, name: cardName(data, 'notifications.theCard') })
        : social('notifications.outbid', { who, name: cardName(data, 'notifications.yourAuction') });
    case 'auction_won':
      return data.buyout
        ? social('notifications.wonBuyout', { name: cardName(data, 'notifications.card') })
        : social('notifications.won', { name: cardName(data, 'notifications.card') });
    case 'auction_sold':
      return data.buyout
        ? social('notifications.soldBuyout', { who, name: cardName(data, 'notifications.yourCard') })
        : social('notifications.sold', { name: cardName(data, 'notifications.card') });
    case 'auction_unsold':
      return social('notifications.unsold', { name: cardName(data, 'notifications.card') });
    case 'trade_received':
      return social('notifications.tradeReceived', { who });
    case 'trade_countered':
      return social('notifications.tradeCountered', { who });
    case 'trade_completed':
      return social('notifications.tradeCompleted', { who });
    case 'trade_declined':
      return social('notifications.tradeDeclined', { who });
    case 'trade_cancelled':
      return social('notifications.tradeCancelled', { who });
    case 'trade_expired':
      return social('notifications.tradeExpired');
    case 'staff_permission_request':
      if (data.field === CATALOG_DELETE_FIELD) return social('notifications.staffDeleteRequest', { who, name: String(data.name ?? '') });
      return social('notifications.staffRequest', {
        who,
        field: i18n.t(`fields.${String(data.field ?? '')}`, { ns: 'admin', defaultValue: String(data.field ?? '') }),
        name: String(data.name ?? ''),
      });
    case 'staff_permission_result':
      if (data.field === CATALOG_DELETE_FIELD) {
        return social(data.decision === 'approved' ? 'notifications.staffDeleteDone' : 'notifications.staffDeleteDenied', {
          name: String(data.name ?? ''),
        });
      }
      return data.decision === 'approved'
        ? social('notifications.staffGranted', {
            field: i18n.t(`fields.${String(data.field ?? '')}`, { ns: 'admin', defaultValue: String(data.field ?? '') }),
            name: String(data.name ?? ''),
          })
        : social('notifications.staffDenied', {
            field: i18n.t(`fields.${String(data.field ?? '')}`, { ns: 'admin', defaultValue: String(data.field ?? '') }),
            name: String(data.name ?? ''),
          });
    default:
      return social('notifications.fallback');
  }
}

export function messageClientId() {
  return crypto.randomUUID().replace(/-/g, '');
}
